<template>
  <VDialogV3
    position="right"
    :entity="localPosition"
    width="600px"
    :title="localPosition.toString()"
    @submit="onSubmit"
  >
    <template v-if="!localPosition.isPreorder">
      <section>
        <header>Образец</header>
        <VSwitch
          v-model="localPosition.isSamples"
          layout="vertical"
          theme="slider"
          :disabled="isFreeSamples"
        />
        <VSwitch
          v-if="localPosition.isSamples"
          v-model="localPosition.isForTestsOnly"
          label="Под производственный план"
          layout="vertical"
          :checked-value="false"
          :unchecked-value="true"
          theme="slider"
        />
      </section>
    </template>

    <hr>

    <section>
      <header>Основное</header>

      <VSelectV2
        v-if="
              localPosition.isExisting === true
           && localPosition.isOfferBased === false
           && localPosition.isDraft === false"
        label="Продавец"
        disabled
        required
        v-model="localPosition.seller"
      />

      <VSelectV2
        v-if="
              localPosition.isExisting === true
              && localPosition.isOfferBased === false
              && localPosition.isDraft === false"
        v-model="localPosition.sellerCompany"
        label="Юр. лицо"
        :disabled="reasonsCannotChangeDealTerms.length > 0"
        :disabled-reasons="reasonsCannotChangeDealTerms"
        required
        :data="sellerCompanySource"
      />

      <VSuggestionSelectV2
        v-if="isFreeSamples"
        v-model="localPosition.product"
        :data="({ pattern }) => ProductsService.search(pattern)"
        label="Товар"
        layout="vertical"
        required
      >
        <template #option="{ option }">
          <div>{{ option.name }}</div>
          <div class="description">
            Артикул: {{ option.code ? option.code : '--' }}
          </div>
        </template>

        <template #helper-text>
          <template v-if="localPosition.product?.isExisting === false">
            Товар не найден и будет создан автоматически
          </template>
        </template>
      </VSuggestionSelectV2>

      <VSelectV2
        v-else
        label="Товар"
        required
        :disabled="reasonsCannotChangeProduct.length > 0"
        :disabled-reasons="reasonsCannotChangeProduct"
        :data="({ pattern }) => ProductsService.search(pattern)"
        v-model="localPosition.product"
      >
        <template #option="{ option }">
          <div>{{ option.name }}</div>
          <div class="description">
            Артикул: {{ option.code ? option.code : '--' }}
          </div>
        </template>
      </VSelectV2>

      <VSelectV2
        :data="productCategorySource"
        label="Категория"
        disabled
        v-model="localPosition.productCategory"
      />

      <VInputV2
        v-model="localPosition.buyersCode"
        label="Артикул"
        :disabled="position?.demandV2?.id !== undefined"
        :validate-method="validateCode"

        :depends-on="() => localPosition.product"
        :default="(product) => product.code"
      />

      <VInputV2
        v-model="localPosition.name"
        label="Наименование товара в ИС поставщика"
        layout="vertical"
        :disabled="reasonsCannotChangeName.length > 0"
        :disabled-reasons="reasonsCannotChangeName"
        required
        :max-length="175"
        :depends-on="() => localPosition.product"
        :default="(product) => product.name"
      />

      <VInput
        v-if="localPosition.isPrinting"
        v-model="localPosition.material"
        label="Материал"
        layout="vertical"
      />

      <VSuggestionSelectV2
        v-if="isFreeSamples"
        v-model="localPosition.manufacturer"
        :data="manufacturerSource"
        label="Производитель"
        layout="vertical"
        required
        clearable
      >
        <template #helper-text>
          <template v-if="localPosition.manufacturer?.isExisting === false">
            Производитель не найден и будет создан автоматически
          </template>
          <template v-else>
            Производитель ID: {{ localPosition.manufacturer?.id ?? 'отсутствует' }}
          </template>
        </template>
      </VSuggestionSelectV2>

      <VSelectV2
        v-else-if="$core.utils.isNotEmptyArray(localPosition.product?.manufacturers) === true"
        :key="localPosition.product.id"
        v-model="localPosition.manufacturer"
        :data="localPosition.product.manufacturers"
        label="Производитель"
        layout="vertical"
        required
      />

      <VSelectV2
        v-else
        v-model="localPosition.manufacturer"
        :data="({ pattern }) => ManufacturersService.search(pattern)"
        label="Производитель"
        layout="vertical"
        required
      />

      <VFormItem
        label="Ссылка (Номер макета)"
        layout="vertical"
      >
        <VInput
          v-model="localPosition.layoutFile"
          layout="vertical"
        />
        <VButton
          theme="link"
          title="Копировать"
          @click="$core.copyToClipboard(localPosition.layoutFile)"
        >
          <template #icon>
            <VIcon material="content_copy" theme="outlined"/>
          </template>
        </VButton>
      </VFormItem>

      <VSwitch
        v-if="localPosition.isPrinting"
        v-model="localPosition.isApproved"
        theme="slider"
        checked-label="Согласовано"
        unchecked-label="Не согласовано"
      >
        Согласовано
      </VSwitch>
    </section>

    <section>
      <VSelectV2
        v-model="localPosition.buyerProductionSite"
        label="Производственная площадка"
        layout="vertical"
        required
        :data="productionSiteSource"
        :disabled="reasonsCannotChangeDealTerms.length > 0"
        :disabled-reasons="reasonsCannotChangeDealTerms"
      />
    </section>

    <hr>

    <section>
      <header>Количество и цена</header>
      <VInputNumberRusV2
        ref="qtyControl"
        label="Количество"
        layout="vertical"
        v-model="localPosition.qty"
        :type="Qty"
        required
        :validate-method="validateQty"
        :unit="localPosition.unit"
      />

      <VFormItem
        label="Цена"
        layout="vertical"
        required
        :unit="`за ${localPosition.unit || '--'}`"
      >
        <VInputNumberRusV2
          v-model="localPosition.priceSeller"
          :type="Price"
          :disabled="reasonsCannotChangePrice.length > 0"
          :disabled-reasons="reasonsCannotChangePrice"
          autocomplete="off"
          required
        />

        <VSelectV2
          v-model="localPosition.currency"
          :data="$core.currencies.currencies"
          option-identity="code"
          :disabled="reasonsCannotChangePrice.length > 0"
          :disabled-reasons="reasonsCannotChangePrice"
          required
        />
      </VFormItem>

      <VInputNumberRusV2
        label="Полная цена"
        layout="vertical"
        v-model="localPosition.priceFullSeller"
        :type="Price"
        disabled
        autocomplete="off"
        required
        :unit="`за ${localPosition.unit ?? ''}`"
      />

      <VFormItem v-if="typeof localPosition.isVatIncludedSeller === 'boolean'">

        <!-- disabled потому что это должно проходить через requisites, и только через заказ -->
        <!--          :disabled-reasons="['Изменение возможно только через заказ, или если позиция заказа по предложению поставщика']"-->
        <VSwitch
          v-model="localPosition.isVatIncludedSeller"
          layout="vertical"
          label="НДС"
          disabled
          checked-label="включительно"
          unchecked-label="сверху"
          theme="slider"
        />

        <VSelectV2
          v-model="localPosition.vatRateSeller"
          layout="vertical"
          label="Ставка"
          :data="$core.dictionaries.vatRates"
          :disabled="reasonsCannotChangePosition.length > 0"
          :disabled-reasons="reasonsCannotChangePosition"
        />
      </VFormItem>

      <VInput
        v-else
        label="НДС"
        layout="vertical"
        disabled
        value="Не облагается"
      />

      <VFormItem>
        <VInputNumberRusV2
          v-model="localPosition.packageVolume"
          label="Фасовка"
          layout="vertical"
          :type="Qty"
          autocomplete="off"
          :required="!localPosition.isPrinting && !Boolean(position?.demandV2)"
          :disabled="!isCategorySelected || localPosition.isPrinting"
          @input="onPackageVolumeChange"
        />

        <VSelectV2
          v-model="localPosition.unit"
          label="Ед. изм."
          layout="vertical"
          :data="unitSource"
          :disabled="isReceived"
          :hide-clear-btn="true"
          required
        />
      </VFormItem>

      <VInputNumberRusV2
        v-if="localPosition.isPreorder"
        label="Страховой запас"
        layout="vertical"
        v-model="localPosition.preorderMonthlyReserve"
        :type="Qty"
        :validate-method="validateQty"
        :unit="localPosition.unit"
      />

      <div
        :class="{ warning:
              localPosition.priceSeller?.isEmpty() !== false
              || localPosition.qty?.isEmpty() !== false
              || !localPosition.currencyCode}"
      >
        <b>Итого: </b>
        <span>{{ totalPriceText }}</span>
      </div>
    </section>

    <OrderPositionComplaintInputs
      v-if="localPosition.isExisting && !localPosition.isPreorder && !localPosition.isDraft"
      :local-position="localPosition"
    />

    <hr>

    <PaymentTermEditor :entity="localPosition" :disabled="$core.currentUser.isAdmin === false"/>

    <hr>

    <section>
      <header>Условия доставки</header>

      <VSelectV2
        v-model="localPosition.deliveryType"
        label="Тип доставки"
        layout="vertical"
        :data="deliveryTypeList"
        :disabled="reasonsCannotChangeDealTerms.length > 0"
        :disabled-reasons="reasonsCannotChangeDealTerms"
        :hide-clear-btn="true"
      />

      <VDateRangeInputNew
        v-if="localPosition.isPreorder"
        v-model="localPosition.preorderShipmentPeriod"
        label="Период доставки"
        layout="vertical"
        required
        :validate-method="(value) => localPosition?.validatePreorderShipmentPeriodFrom(value)"
      />

      <template v-else>
        <VDateInput
          v-model="localPosition.desiredReceiptDate"
          label="Желаемая дата доставки"
          layout="vertical"
          required
          :validate-method="(value) => localPosition.validateDesiredReceiptDate(value)"
          :warning-method="(value) => warnWeekend(value)"
        >
          <template #helper-text>
            {{ $core.utils.date.getWeekDayName(localPosition.desiredReceiptDate) }}
          </template>
        </VDateInput>

        <VDateInput
          v-model="localPosition.plannedShipmentDate"
          label="Планируемая дата отгрузки"
          layout="vertical"
          required
          :warning-method="(value) => warnWeekend(value)"
        >
          <template #helper-text>
            {{ $core.utils.date.getWeekDayName(localPosition.plannedShipmentDate) }}
          </template>
        </VDateInput>
      </template>

      <VTemperatureRangeInput
        v-if="localPosition.isPrinting === false"
        v-model="localPosition.storeTemperatureRange"
        label="Температурный режим перевозки, °C"
        with-asterisk
        required
        :min="-60"
        :max="70"
      />

      <VTextarea
        v-model="localPosition.storeRulesText"
        label="Особые условия перевозки"
        layout="vertical"
        :disabled="reasonsCannotChangePosition.length > 0"
        :disabled-reasons="reasonsCannotChangePosition"
        :max-length="300"
      />
    </section>

    <hr v-if="showReasonInput">

    <section v-if="showReasonInput">
      <header>Причина редактирования</header>
      <VTextarea
        v-model="reasonText"
        placeholder="Введите причину редактирования"
        layout="vertical"
        :max-length="500"
      />
    </section>
  </VDialogV3>
</template>

<script lang="ts">
import Qty from '@/core/types/Qty';
import Price from '@/core/types/Price';
import ProductionSitesService from '@/domains/ProductionSites/ProductionSitesService';
import CompaniesService from '@/domains/Companies/CompaniesService';
import UnitsService from '@/domains/Units/UnitsService';
import DeliveryTypesService from '@/domains/DeliveryTypes/DeliveryTypesService';
import PaymentTermEditor
  from '@/domains/PaymentTerms/components/PaymentTermEditor/PaymentTermEditor.vue';
import ProductsService from '@/domains/Products/ProductsService';
import ManufacturersService from '@/domains/Manufacturers/ManufacturersService';
import OrderPosition from '@/domains/Orders/OrderPosition';
import OrderPositionComplaintInputs
  from "@/domains/Orders/components/OrderPositionComplaintInputs/OrderPositionComplaintInputs.vue";
import type Order from '@/domains/Orders/Order';
import PreorderPosition from "@/domains/Preorders/PreorderPosition";

export default defineComponent({
  name: 'OrderPositionRequisites',

  components: {
    PaymentTermEditor,
    OrderPositionComplaintInputs,
  },

  props: {
    /**
     * Существующая позиция для редактирования. Не передаётся при создании новой.
     */
    position: {
      type: Object as PropType<OrderPosition | PreorderPosition>,
      default: null,
    },
    /**
     * Заказ, к которому относится позиция. Обязателен:
     *  - при создании (position === null) для OrderPosition.fromOrder(order);
     *  - при редактировании — для контекста (категория, флаги, валюта).
     */
    order: {
      type: Object as PropType<Order>,
      default: null,
    },
    /* Поле скрывающее причину изменения,
     * это нужно когда позиция редактируется в контексте заказа
     */
    showReasonInput: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['save'],

  setup() {
    return {
      Qty,
      Price,
      ProductsService,
      ManufacturersService,
    };
  },

  data() {
    /**
     * localPosition — позиция, с которой работает форма.
     *  - если position передан → клонируем (редактирование);
     *  - если не передан → создаём через фабрику от заказа.
     */
    let localPosition: OrderPosition|PreorderPosition;
    if (this.position !== null) {
      localPosition = this.$core.utils.cloneDeepWithReference(this.position, ['order']);
    } else if (this.order) {
      localPosition = this.order.isPreorder === true ? new PreorderPosition() : new OrderPosition();

      localPosition.setOrder(this.order);
    }

    const sellerCompanySource = this.$core.loader.select(({ seller }) => CompaniesService.getList({ partnerId: seller.id }))
      .dependsOn({ seller: () => this.localPosition.seller })
      .preloadOnFocus();

    /**
     * Производители: если у товара они перечислены — выбираем строго из них,
     * иначе ввод уходит в глобальный поиск по справочнику.
     *
     * Зависимость от товара нужна не только ради перезагрузки при его смене:
     * без выбранного товара выбирать не из чего, и селект гаснет сам.
     */
    const manufacturerSource = this.$core.loader.select(({ product, pattern }) => {
      if (this.$core.utils.isNotEmptyArray(product?.manufacturers) === true) {
        return product.manufacturers;
      } else {
        return ManufacturersService.search(pattern);
      }
    })
      .dependsOn({ product: () => this.localPosition.product })
      .preloadOnFocus();

    /**
     * Производственные площадки — по юр. лицу покупателя.
     */
    const productionSiteSource = this.$core.loader.select(({ buyerCompany }) => ProductionSitesService.getList(null, buyerCompany.id))
      .dependsOn({ buyerCompany: () => this.localPosition.buyerCompany })
      .preloadOnMount();

    /**
     * Единицы измерения зависят от категории товара:
     *  - freeSamples без выбранной категории → весь справочник единиц;
     *  - иначе → allowedUnits выбранной категории.
     */
    const unitSource = this.$core.loader.select(({ productCategory }) => {
      if (this.localPosition.orderCategory.id === 'freeSamples'
        && !this.$core.utils.isObject(productCategory)) {
        return UnitsService.getList();
      } else {
        return this.$core.utils.isObject(productCategory) ? productCategory.allowedUnits : [];
      }
    })
      .dependsOn({ productCategory: () => this.localPosition.productCategory })
      .preloadOnMount();

    const productCategorySource = this.$core.loader.select(({ product }) => {
      // VSuggestionSelect создаёт пустышку, у которой нет категории.
      // нужно вернуть значение, только если мы нашли продукт на беке
      if (this.$core.utils.isNullOrUndefined(product?.category)) return []
      return [product.category]
    })
      .dependsOn({ product: () => this.localPosition.product })

    return {
      localPosition,

      sellerCompanySource,
      manufacturerSource,
      productionSiteSource,
      unitSource,
      productCategorySource,

      reasonText: null as string | null,
    };
  },

  computed: {
    /**
     * Для позиций что были приняты нужно разрешить менять даты пдо и ждд, но
     * так как, по всей видимости, чем больше позиция прошла жизненных хуков,
     * тем меньше доступных реквизитов к редактированию, и нельзя сделать одно
     * computed свойство, отвечающее за все поля сразу
     */
    isReceived(): boolean {
      if (!this.localPosition.isExisting) return false;
      return Boolean(this.localPosition?.shipmentStatus?.receivedByBuyer?.isPositive());
    },

    /** при неявном создании товара в категории Бесплатные образцы productCategory будет пустым */
    isCategorySelected(): boolean {
      if (this.localPosition.orderCategory.id === 'freeSamples'
        && !this.$core.utils.isObject(this.localPosition.productCategory)) {
        return true;
      }
      return (
        this.$core.utils.isObject(this.localPosition.productCategory)
        && this.$core.utils.number.isNumber(this.localPosition.productCategory.id)
      );
    },

    deliveryTypeList() {
      return DeliveryTypesService.getListForOrderPosition(this.localPosition.isOfferBased);
    },

    totalPriceText(): string {
      const hasPrice = this.localPosition.priceSeller?.isEmpty() === false;
      const hasQty = this.localPosition.qty?.isEmpty() === false;
      const hasCurrency = Boolean(this.localPosition?.currencyCode);

      if (!hasPrice || !hasQty || !hasCurrency) {
        return 'Введите цену, валюту и количество';
      }

      const sumFull = this.localPosition.sumFullSeller.format(this.localPosition.currency);

      if (this.localPosition.vatSumSeller === null) {
        return `${sumFull}, НДС не облагается`;
      }

      const vatSum = this.localPosition.vatSumSeller.format(this.localPosition.currency);

      return `${sumFull}, в том числе НДС ${vatSum}`;
    },

    isFreeSamples(): boolean {
      return this.localPosition.orderCategory.id === 'freeSamples';
    },

    // ==================== Причины недоступности контролов ====================
    // Источников причин три, по категориям полей, а не по контролам:
    // собственные поля позиции, товар и реквизиты сделки. Новое поле формы
    // относят к одной из категорий — отдельного computed оно не требует.
    // Пустой массив — контрол доступен.

    /** Собственные поля позиции: цена, валюта, ставка НДС, условия перевозки. */
    reasonsCannotChangePosition(): string[] {
      return this.localPosition.getReasonsCannotChangePosition();
    },

    /** Товар позиции. */
    reasonsCannotChangeProduct(): string[] {
      return this.localPosition.getReasonsCannotChangeProduct();
    },

    /**
     * Реквизиты сделки — общие для заказа и всех его позиций: юр. лицо
     * продавца, производственная площадка, тип доставки.
     *
     * Правятся в заказе, а не в позиции.
     *
     * Исключение — предзаказ и заказ из предзаказа:
     * определяем по позиции, реквизиты её происхождения не знают.
     */
    reasonsCannotChangeDealTerms(): string[] {
      return this.localPosition.getReasonsCannotChangeDealTerms();
    },

    // Ниже — не права, а отсутствие данных для ввода.

    /** Наименование в ИС поставщика подставляется из товара. */
    reasonsCannotChangeName(): string[] {
      const reasons = [...this.reasonsCannotChangePosition];

      if (this.$core.utils.isNullOrUndefined(this.localPosition.product) === true) {
        reasons.push('Не выбран товар');
      }

      return reasons;
    },

    /** У бесплатных образцов цены нет по определению. */
    reasonsCannotChangePrice(): string[] {
      const reasons = [...this.reasonsCannotChangePosition];

      if (this.isFreeSamples === true) {
        reasons.push('Бесплатные образцы');
      }

      return reasons;
    },
  },

  methods: {
    /**
     * Валидация-варнинг: проверяет, выпадает ли дата на выходной.
     * Контрол сам показывает варнинг в messages-row жёлтым.
     */
    warnWeekend(value: unknown): string | null {
      if (!value) return null;
      if (this.$core.utils.date.isWeekend(value) === false) return null;
      return `Выпадает на выходной день (${this.$core.utils.date.getWeekDayName(value, true, true)})`;
    },

    /**
     * Валидация количества с учётом фасовки и единицы измерения.
     * value — Qty из VInputNumberRusV2. Валидация количества это инвариант
     * позиции (кратность фасовке, категория сырья), а не свойство числа —
     * поэтому живёт в утилите, а не на Qty.
     * Метод не переносить в шаблон — шаблон это декларативное описание UI,
     * не место для логики.
     */
    validateQty(value: Qty): string | null {
      return this.$core.utils.number.validateDesiredQty(
        value,
        this.localPosition.packageVolume,
        undefined,
        undefined,
        this.localPosition.unit,
        this.localPosition.isRawMaterialsAndPackage,
      );
    },

    validateCode(value: string): string | null | undefined {
      if (this.localPosition.orderCategory.id === 'freeSamples'
        && !this.$core.utils.isObject(this.localPosition.productCategory)) {
        return;
      }
      if (!value) {
        return 'Необходимо выбрать товар содержащий артикул';
      }
    },

    /**
     * Перевалидируем qty при смене фасовки — validateQty зависит от packageVolume,
     * VBaseControl сам этого не отследит, поэтому дёргаем ref на контрол вручную.
     */
    onPackageVolumeChange(): void {
      const qtyControl = this.$refs.qtyControl as any;
      if (
        this.$core.utils.isObject(qtyControl)
        && typeof qtyControl.validateForce === 'function'
        && this.localPosition.qty
      ) {
        qtyControl.validateForce();
      }
    },

    onSubmit(): void {
      if (this.showReasonInput) {
        this.$emit('save', {
          position: this.localPosition,
          reasonText: this.reasonText,
        });
      } else {
        this.$emit('save', {position: this.localPosition});
      }
    },
  },
});
</script>
